import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Divider,
  LinearProgress,
  Chip
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TimelineIcon from '@mui/icons-material/Timeline';
import RepeatIcon from '@mui/icons-material/Repeat';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';

/**
 * FrequencyMetrics Component
 * Displays detailed frequency metrics for a parameter in an expanded row
 *
 * @param {object} props
 * @param {object} props.data - Frequency data containing coverage, volume, and pattern info
 * @param {string} props.availableSince - Date when parameter became available
 * @param {string} props.sensitivity - Sensitivity level
 * @param {string} props.usagePattern - Usage pattern description
 */
const FrequencyMetrics = ({ data, availableSince, sensitivity, usagePattern }) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('pt')
    ? 'pt-PT'
    : (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('de')
      ? 'de-DE'
      : 'en-US';
  const suppressed = Boolean(data?.suppressed);
  const suppressionThreshold = data?.suppressionThreshold;
  const caseCoverage = data?.caseCoverage;
  const totalCases = data?.totalCases;
  const casesWithParameter = data?.casesWithParameter;
  const stationCoverage = Array.isArray(data?.stationCoverage) ? data.stationCoverage : [];
  const avgMeasurementsPerCase = data?.avgMeasurementsPerCase;
  const medianMeasurementsPerCase = data?.medianMeasurementsPerCase;
  const totalMeasurements = data?.totalMeasurements;
  const measurementPattern =
    data?.measurementPattern && typeof data.measurementPattern === 'object'
      ? data.measurementPattern
      : {};
  const continuousCases = data?.continuousCases;

  const hasNumber = (value) => typeof value === 'number' && Number.isFinite(value);
  const formatNumber = (value) => (hasNumber(value) ? value.toLocaleString(locale) : '–');
  const formatMeasurementValue = (value) => (hasNumber(value) ? value : '–');

  if (suppressed) {
    // #102: die ganze Kennzahlengruppe stammt aus derselben Aggregation --
    // liegt die Fallzahl unter der Schwelle, ist keine der drei Spalten eine
    // echte Zahl. Eine 0 oder ein leeres Feld waere eine falsche Aussage,
    // deshalb eine eigene, unverwechselbare Darstellung statt der Kacheln.
    const hasThreshold = hasNumber(suppressionThreshold);
    const explanation = hasThreshold
      ? t('dataCatalog.frequency.suppressedExplanationWithThreshold', { threshold: suppressionThreshold })
      : t('dataCatalog.frequency.suppressedExplanation');

    return (
      <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <VisibilityOffIcon sx={{ color: '#616161' }} />
          <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">
            {t('dataCatalog.frequency.suppressed')}
          </Typography>
        </Box>
        <Typography variant="body2" color="textSecondary">
          {explanation}
        </Typography>
      </Box>
    );
  }

  /**
   * Group stations by category/area
   */
  const groupStationsByArea = (stations) => {
    const groups = {
      [t('dataCatalog.frequency.stationGroups.icu')]: { stations: [], totalPercentage: 0, percentageCount: 0, color: '#1976d2' },
      [t('dataCatalog.frequency.stationGroups.imc')]: { stations: [], totalPercentage: 0, percentageCount: 0, color: '#42a5f5' },
      [t('dataCatalog.frequency.stationGroups.ward')]: { stations: [], totalPercentage: 0, percentageCount: 0, color: '#90caf9' },
      [t('dataCatalog.frequency.stationGroups.opDiagnostics')]: { stations: [], totalPercentage: 0, percentageCount: 0, color: '#4caf50' },
      [t('dataCatalog.frequency.stationGroups.lab')]: { stations: [], totalPercentage: 0, percentageCount: 0, color: '#ff9800' },
      [t('dataCatalog.frequency.stationGroups.other')]: { stations: [], totalPercentage: 0, percentageCount: 0, color: '#9e9e9e' }
    };

    // Eine Station ohne (oder mit nicht-numerischer) `percentage` darf den
    // Gruppendurchschnitt nicht als echte 0 herunterziehen -- sie zaehlt
    // deshalb nicht in `percentageCount` mit (Rueckmeldung Runde 2, NEU B1).
    const addToGroup = (group, station) => {
      group.stations.push(station);
      if (hasNumber(station.percentage)) {
        group.totalPercentage += station.percentage;
        group.percentageCount += 1;
      }
    };

    stations.forEach(station => {
      if (!station?.name) {
        return;
      }

      if (station.name.includes('ICU') || station.name.includes('NICU') || station.name.includes('PICU')) {
        addToGroup(groups[t('dataCatalog.frequency.stationGroups.icu')], station);
      } else if (station.name.includes('IMC')) {
        addToGroup(groups[t('dataCatalog.frequency.stationGroups.imc')], station);
      } else if (station.name.includes('Station') || station.name.includes('Stroke Unit')) {
        addToGroup(groups[t('dataCatalog.frequency.stationGroups.ward')], station);
      } else if (station.name.includes('OP') || station.name.includes('Anästhesie') || station.name.includes('Diagnostik')) {
        addToGroup(groups[t('dataCatalog.frequency.stationGroups.opDiagnostics')], station);
      } else if (station.name.includes('Labor')) {
        addToGroup(groups[t('dataCatalog.frequency.stationGroups.lab')], station);
      } else {
        addToGroup(groups[t('dataCatalog.frequency.stationGroups.other')], station);
      }
    });

    // Calculate average percentage per area -- keine Angabe (null), wenn
    // keine einzige Station der Gruppe eine Prozentzahl mitbrachte.
    Object.keys(groups).forEach(key => {
      const group = groups[key];
      group.avgPercentage =
        group.percentageCount > 0 ? Math.round(group.totalPercentage / group.percentageCount) : null;
    });

    // Return as sorted array (by avgPercentage descending, Gruppen ohne
    // Angabe zuletzt)
    return Object.entries(groups)
      .map(([name, data]) => ({ name, ...data }))
      .filter(group => group.stations.length > 0)
      .sort((a, b) => (hasNumber(b.avgPercentage) ? b.avgPercentage : -1) - (hasNumber(a.avgPercentage) ? a.avgPercentage : -1));
  };

  const stationGroups = groupStationsByArea(stationCoverage);
  const continuousPercentage =
    hasNumber(continuousCases) && hasNumber(casesWithParameter) && casesWithParameter > 0
      ? `${((continuousCases / casesWithParameter) * 100).toFixed(0)}%`
      : '–';
  const measurementPatternTypical = measurementPattern?.typical || t('dataCatalog.frequency.noData');
  const hasStationData = stationGroups.length > 0;
  const hasCaseData = hasNumber(caseCoverage) || hasNumber(totalCases) || hasNumber(casesWithParameter);
  const hasVolumeData =
    hasNumber(avgMeasurementsPerCase) ||
    hasNumber(medianMeasurementsPerCase) ||
    hasNumber(totalMeasurements);

  return (
    <Box sx={{ p: 2, backgroundColor: '#fafafa' }}>
      <Grid container spacing={3}>
        {/* 1. Coverage / Verbreitung */}
        <Grid item xs={12} md={4}>
          <Box sx={{
            borderLeft: '4px solid #1976d2',
            pl: 2,
            backgroundColor: '#f5f9ff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOnIcon sx={{ color: '#1976d2' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1976d2' }}>
                {t('dataCatalog.frequency.coverage')}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {hasStationData
                  ? t('dataCatalog.frequency.stationAreas', { count: stationGroups.length })
                  : t('dataCatalog.frequency.noStationData')}
              </Typography>
              {hasStationData ? (
                <>
                  {stationGroups.slice(0, 3).map((group, idx) => (
                    <Box key={idx} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography variant="caption" fontWeight={500}>
                          {group.name} ({group.stations.length})
                        </Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {hasNumber(group.avgPercentage) ? `${group.avgPercentage}%` : '–'}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={hasNumber(group.avgPercentage) ? group.avgPercentage : 0}
                        sx={{
                          height: 6,
                          borderRadius: 1,
                          backgroundColor: '#e0e0e0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: group.color
                          }
                        }}
                      />
                    </Box>
                  ))}
                  {stationGroups.length > 3 && (
                    <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                      {t('dataCatalog.frequency.moreAreas', { count: stationGroups.length - 3 })}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="caption" color="textSecondary">
                  {t('dataCatalog.frequency.noDataAvailable')}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.caseCoverage')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {hasNumber(caseCoverage) ? t('dataCatalog.frequency.allCases', { value: caseCoverage }) : '–'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {hasCaseData
                  ? t('dataCatalog.frequency.casesOfTotal', {
                      cases: formatNumber(casesWithParameter),
                      total: formatNumber(totalCases),
                    })
                  : t('dataCatalog.frequency.noCaseData')}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* 2. Volume / Messumfang */}
        <Grid item xs={12} md={4}>
          <Box sx={{
            borderLeft: '4px solid #2e7d32',
            pl: 2,
            backgroundColor: '#f5fef5'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TimelineIcon sx={{ color: '#2e7d32' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                {t('dataCatalog.frequency.volume')}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.averagePerCase')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {hasNumber(avgMeasurementsPerCase)
                  ? t('dataCatalog.frequency.measurementsAverage', { value: formatNumber(avgMeasurementsPerCase) })
                  : '–'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {hasVolumeData
                  ? t('dataCatalog.frequency.median', { value: formatMeasurementValue(medianMeasurementsPerCase) })
                  : t('dataCatalog.frequency.noMeasurementData')}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.total')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatNumber(totalMeasurements)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {hasVolumeData
                  ? t('dataCatalog.frequency.measurementsLastYear')
                  : t('dataCatalog.frequency.noMeasurementData')}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* 3. Pattern / Messmuster */}
        <Grid item xs={12} md={4}>
          <Box sx={{
            borderLeft: '4px solid #ed6c02',
            pl: 2,
            backgroundColor: '#fff8f5'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <RepeatIcon sx={{ color: '#ed6c02' }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#ed6c02' }}>
                {t('dataCatalog.frequency.pattern')}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.typicalFrequency')}
              </Typography>
              <Chip
                label={measurementPatternTypical}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ mb: 0.5 }}
              />
              <Typography variant="caption" display="block" color="textSecondary">
                {t('dataCatalog.frequency.interval', { value: measurementPattern.avgInterval || '–' })}
              </Typography>
              <Typography variant="caption" display="block" color="textSecondary">
                σ {measurementPattern.stdDev || '–'}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {t('dataCatalog.frequency.continuousMeasurements')}
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {continuousPercentage}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {hasNumber(continuousCases)
                  ? t('dataCatalog.frequency.continuousCases', { count: formatNumber(continuousCases) })
                  : t('dataCatalog.frequency.noData')}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

    </Box>
  );
};

export default FrequencyMetrics;
